import { useEffect, useRef } from 'react';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { Button } from './Button';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'primary' | 'danger';
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  isLoading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  useEscapeClose(() => { if (!isLoading) onCancel(); }, isOpen);

  useEffect(() => {
    if (isOpen) confirmButtonRef.current?.focus();
  }, [isOpen]);

  // Prevent body scroll when dialog is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // The destructive confirm wears the theme's own deep red (feature 023). Every dialog that
  // discards something — leaving a session, logging out mid-session, removing a problem from
  // review, clearing history — shares this button, so they all changed together.
  const confirmClasses =
    confirmVariant === 'danger'
      ? 'bg-danger hover:bg-danger-hover text-white px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50'
      : '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={!isLoading ? onCancel : undefined}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className="relative bg-card rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
      >
        <h2
          id="dialog-title"
          className="text-lg font-semibold text-ink mb-2"
        >
          {title}
        </h2>
        <p className="text-ink-soft mb-6">{message}</p>

        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancelLabel}
          </Button>
          {confirmVariant === 'danger' ? (
            <button
              ref={confirmButtonRef}
              className={confirmClasses}
              onClick={onConfirm}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : confirmLabel}
            </button>
          ) : (
            <button
              ref={confirmButtonRef}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
              onClick={onConfirm}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
